// src/views/parent/PASituationFrais.jsx
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useParentService } from "../../services/parentService";
import { useParentDrawer } from "../../context/ParentDrawerContext";

// ─── HELPERS ──────────────────────────────────────────────────────────────
function formatMontant(valeur) {
  if (valeur === null || valeur === undefined) return "0";
  const n = parseFloat(valeur);
  if (isNaN(n)) return "0";
  return n.toLocaleString("fr-FR", { maximumFractionDigits: 0 });
}

function getInitiales(nom) {
  if (!nom) return "?";
  const parts = nom.trim().split(" ");
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return nom[0].toUpperCase();
}

function getStatutMeta(statut) {
  switch (statut) {
    case "VALIDE":  return { label: "Validé",   color: "var(--gn)", bg: "rgba(59,109,17,0.1)",  icon: "ti ti-circle-check" };
    case "ANNULE":  return { label: "Annulé",   color: "var(--rd)", bg: "rgba(220,53,69,0.1)",  icon: "ti ti-circle-x" };
    default:        return { label: statut || "-", color: "var(--am)", bg: "rgba(186,117,23,0.1)", icon: "ti ti-hourglass" };
  }
}

function formatDate(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return `${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1).toString().padStart(2, "0")}/${d.getFullYear()}`;
}

// Barre de progression dû/payé
function ProgressionBar({ paye, du }) {
  const pct = du > 0 ? Math.min(100, Math.round((paye / du) * 100)) : 0;
  const color = pct >= 100 ? "var(--gn)" : pct >= 50 ? "var(--or)" : "var(--rd)";
  return (
    <div>
      <div style={{
        height: 8, borderRadius: 4, background: "var(--b)", overflow: "hidden",
      }}>
        <div style={{
          width: `${pct}%`, height: "100%", background: color,
          borderRadius: 4, transition: "width 0.4s ease",
        }} />
      </div>
      <div style={{
        display: "flex", justifyContent: "space-between", marginTop: 6,
        fontFamily: "Inter, sans-serif", fontSize: 11, color: "var(--m)",
      }}>
        <span>{pct}% payé</span>
        <span>{formatMontant(paye)} / {formatMontant(du)} FCFA</span>
      </div>
    </div>
  );
}

// ─── SKELETON ─────────────────────────────────────────────────────────────
function SkeletonLoader() {
  return (
    <div style={{ background: "var(--s2)", minHeight: "100dvh" }}>
      <div style={{
        height: 230, borderRadius: "0 0 40px 40px",
        background: "linear-gradient(135deg, var(--pr-bg), var(--pr-lt))",
        animation: "skPulse 1.2s ease-in-out infinite alternate",
      }} />
      <div style={{ padding: 16, marginTop: 16, display: "flex", flexDirection: "column", gap: 12 }}>
        {[1, 2, 3].map(i => (
          <div key={i} style={{
            height: 180, borderRadius: 20, background: "var(--b2)",
            animation: "skPulse 1.2s ease-in-out infinite alternate",
            animationDelay: `${i * 0.15}s`,
          }} />
        ))}
      </div>
    </div>
  );
}

// ─── HEADER ───────────────────────────────────────────────────────────────
function Header({ openDrawer }) {
  return (
    <div style={{ position: "relative" }}>
      <div style={{
        padding: "52px 16px 40px",
        background: "linear-gradient(135deg, var(--pr-dk), var(--pr))",
        borderRadius: "0 0 40px 40px",
        boxShadow: "0 4px 15px rgba(0,0,0,0.15)",
        display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
        position: "relative", overflow: "hidden",
      }}>
        <i className="ti ti-wallet" style={{ color: "#fff", fontSize: 60 }} />
        <div style={{
          fontFamily: "Poppins, sans-serif", color: "#fff",
          fontSize: 24, fontWeight: 700, textAlign: "center",
        }}>
          Situation financière
        </div>
        <div style={{
          fontFamily: "Inter, sans-serif", color: "rgba(255,255,255,0.7)",
          fontSize: 14, textAlign: "center",
        }}>
          Suivez les versements
        </div>

        <div style={{
          position: "absolute", left: "20%", right: "20%", bottom: 0,
          height: 3,
          background: "linear-gradient(90deg, transparent, var(--or), transparent)",
        }} />
      </div>

      <button
        onClick={openDrawer}
        aria-label="Ouvrir le menu"
        style={{
          position: "absolute", top: 44, left: 16,
          background: "rgba(255,255,255,0.2)", border: "none",
          borderRadius: 12, width: 42, height: 42, cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        <i className="ti ti-menu-2" style={{ color: "#fff", fontSize: 26 }} />
      </button>
    </div>
  );
}

// ─── BANDEAU ENFANT UNIQUE ────────────────────────────────────────────────
function EnfantInfoBar({ situation }) {
  const nomComplet = situation.eleve || `${situation.eleve_prenom || ""} ${situation.eleve_nom || ""}`.trim();
  const classeNom = situation.classe_nom || situation.classe || "-";

  return (
    <div style={{
      margin: 16, padding: 16, background: "#fff", borderRadius: 16,
      boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
      display: "flex", alignItems: "center", gap: 16,
    }}>
      <div style={{
        width: 50, height: 50, borderRadius: "50%",
        background: "var(--pr-bg)",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: "Poppins, sans-serif", fontSize: 20, fontWeight: 700,
        color: "var(--pr)", flexShrink: 0,
      }}>
        {getInitiales(nomComplet)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: "Poppins, sans-serif", fontSize: 16, fontWeight: 700, color: "var(--t)" }}>
          {nomComplet}
        </div>
        <div style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "var(--m)", marginTop: 2 }}>
          Classe : {classeNom}
        </div>
      </div>
    </div>
  );
}

// ─── STATS GLOBALES ───────────────────────────────────────────────────────
function StatsGlobales({ situations }) {
  let totalDu = 0, totalPaye = 0;
  for (const s of situations) {
    totalDu += parseFloat(s.total_du || s.montant_total_du || 0);
    totalPaye += parseFloat(s.total_paye || s.montant_total_paye || 0);
  }
  const reste = totalDu - totalPaye;
  const resteColor = reste <= 0 ? "var(--gn)" : "var(--rd)";

  return (
    <div style={{
      margin: 16, padding: 20, background: "#fff", borderRadius: 20,
      boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 12,
          background: "rgba(27,79,128,0.1)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <i className="ti ti-chart-pie" style={{ fontSize: 22, color: "var(--pr)" }} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "Poppins, sans-serif", fontSize: 15, fontWeight: 700, color: "var(--t)" }}>
            Vue d'ensemble
          </div>
          <div style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "var(--m)" }}>
            {situations.length} enfant{situations.length > 1 ? "s" : ""}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <div style={{
          flex: 1, padding: "10px 12px", borderRadius: 12,
          background: "rgba(27,79,128,0.06)",
        }}>
          <div style={{ fontFamily: "Inter, sans-serif", fontSize: 11, color: "var(--m)", marginBottom: 4 }}>
            Total dû
          </div>
          <div style={{ fontFamily: "Poppins, sans-serif", fontSize: 16, fontWeight: 700, color: "var(--t)" }}>
            {formatMontant(totalDu)}
          </div>
          <div style={{ fontFamily: "Inter, sans-serif", fontSize: 10, color: "var(--h)" }}>
            FCFA
          </div>
        </div>
        <div style={{
          flex: 1, padding: "10px 12px", borderRadius: 12,
          background: "rgba(59,109,17,0.06)",
        }}>
          <div style={{ fontFamily: "Inter, sans-serif", fontSize: 11, color: "var(--m)", marginBottom: 4 }}>
            Total payé
          </div>
          <div style={{ fontFamily: "Poppins, sans-serif", fontSize: 16, fontWeight: 700, color: "var(--gn)" }}>
            {formatMontant(totalPaye)}
          </div>
          <div style={{ fontFamily: "Inter, sans-serif", fontSize: 10, color: "var(--h)" }}>
            FCFA
          </div>
        </div>
        <div style={{
          flex: 1, textAlign: "center", padding: "10px 8px", borderRadius: 12,
          background: reste <= 0 ? "rgba(59,109,17,0.06)" : "rgba(220,53,69,0.06)",
        }}>
          <div style={{ fontFamily: "Inter, sans-serif", fontSize: 11, color: "var(--m)", marginBottom: 4 }}>
            Reste
          </div>
          <div style={{ fontFamily: "Poppins, sans-serif", fontSize: 16, fontWeight: 700, color: resteColor }}>
            {formatMontant(reste)}
          </div>
          <div style={{ fontFamily: "Inter, sans-serif", fontSize: 10, color: "var(--h)" }}>
            FCFA
          </div>
        </div>
      </div>

      <ProgressionBar paye={totalPaye} du={totalDu} />
    </div>
  );
}

// ─── CARTE ENFANT ─────────────────────────────────────────────────────────
function EnfantCard({ situation, slug, navigate }) {
  const [versements, setVersements] = useState([]);
  const [loadingVersements, setLoadingVersements] = useState(true);

  const { getVersementsEnfant } = useParentService();

  const eleveId = situation.eleve_id;
  const nomComplet = situation.eleve || `${situation.eleve_prenom || ""} ${situation.eleve_nom || ""}`.trim();
  const classeNom = situation.classe_nom || situation.classe || "-";
  const totalDu = parseFloat(situation.total_du || situation.montant_total_du || 0);
  const totalPaye = parseFloat(situation.total_paye || situation.montant_total_paye || 0);
  const reste = totalDu - totalPaye;
  const statut = situation.statut || "EN_COURS";

  useEffect(() => {
    if (statut === "AUCUNE_INSCRIPTION" || !eleveId) return;
    let annule = false;
    const fetchVersements = async () => {
      setLoadingVersements(true);
      try {
        const data = await getVersementsEnfant(slug, eleveId);
        if (!annule) setVersements(data || []);
      } catch (err) {
      } finally {
        if (!annule) setLoadingVersements(false);
      }
    };
    fetchVersements();
    return () => { annule = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, eleveId, statut]);

  // Pas d'inscription → affichage spécial
  if (statut === "AUCUNE_INSCRIPTION") {
    return (
      <div style={{
        background: "#fff", borderRadius: 20, padding: 20, marginBottom: 16,
        boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
        display: "flex", alignItems: "center", gap: 14,
      }}>
        <div style={{
          width: 50, height: 50, borderRadius: "50%",
          background: "linear-gradient(135deg, var(--pr), var(--or))",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontFamily: "Poppins, sans-serif", color: "#fff", fontWeight: 700, fontSize: 16,
          flexShrink: 0,
        }}>
          {getInitiales(nomComplet)}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "Poppins, sans-serif", fontSize: 15, fontWeight: 700, color: "var(--t)" }}>
            {nomComplet}
          </div>
          <div style={{
            marginTop: 6, display: "inline-flex", alignItems: "center", gap: 4,
            padding: "4px 10px", borderRadius: 12, background: "var(--s2)",
            fontFamily: "Inter, sans-serif", fontSize: 11, color: "var(--h)",
          }}>
            <i className="ti ti-info-circle" style={{ fontSize: 12 }} />
            Aucune inscription
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: "#fff", borderRadius: 20, marginBottom: 16,
      boxShadow: "0 4px 10px rgba(0,0,0,0.05)", overflow: "hidden",
    }}>
      {/* En-tête enfant */}
      <div style={{
        padding: 20,
        background: "linear-gradient(135deg, rgba(27,79,128,0.05), rgba(192,138,46,0.02))",
        display: "flex", alignItems: "center", gap: 14,
      }}>
        <div style={{
          width: 55, height: 55, borderRadius: "50%",
          background: "linear-gradient(135deg, var(--pr), var(--or))",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontFamily: "Poppins, sans-serif", color: "#fff", fontWeight: 700, fontSize: 18,
          flexShrink: 0,
        }}>
          {getInitiales(nomComplet)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: "Poppins, sans-serif", fontSize: 16, fontWeight: 700, color: "var(--t)" }}>
            {nomComplet}
          </div>
          <div style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "var(--m)", marginTop: 2 }}>
            {classeNom}
          </div>
        </div>
      </div>

      {/* Bloc financier */}
      <div style={{ padding: 16 }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
          <div style={{ flex: 1, textAlign: "center", padding: "10px 8px", borderRadius: 12, background: "rgba(27,79,128,0.06)" }}>
            <div style={{ fontFamily: "Inter, sans-serif", fontSize: 10, color: "var(--m)", marginBottom: 4 }}>
              Dû
            </div>
            <div style={{ fontFamily: "Poppins, sans-serif", fontSize: 15, fontWeight: 700, color: "var(--t)" }}>
              {formatMontant(totalDu)}
            </div>
          </div>
          <div style={{ flex: 1, textAlign: "center", padding: "10px 8px", borderRadius: 12, background: "rgba(59,109,17,0.06)" }}>
            <div style={{ fontFamily: "Inter, sans-serif", fontSize: 10, color: "var(--m)", marginBottom: 4 }}>
              Payé
            </div>
            <div style={{ fontFamily: "Poppins, sans-serif", fontSize: 15, fontWeight: 700, color: "var(--gn)" }}>
              {formatMontant(totalPaye)}
            </div>
          </div>
          <div style={{
            flex: 1, textAlign: "center", padding: "10px 8px", borderRadius: 12,
            background: reste <= 0 ? "rgba(59,109,17,0.06)" : "rgba(220,53,69,0.06)",
          }}>
            <div style={{ fontFamily: "Inter, sans-serif", fontSize: 10, color: "var(--m)", marginBottom: 4 }}>
              Reste
            </div>
            <div style={{
              fontFamily: "Poppins, sans-serif", fontSize: 15, fontWeight: 700,
              color: reste <= 0 ? "var(--gn)" : "var(--rd)",
            }}>
              {formatMontant(reste)}
            </div>
          </div>
        </div>

        <ProgressionBar paye={totalPaye} du={totalDu} />

        {/* Historique des versements */}
        <div style={{ marginTop: 18 }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 6, marginBottom: 10,
            fontFamily: "Inter, sans-serif", fontSize: 13, fontWeight: 600, color: "var(--t)",
          }}>
            <i className="ti ti-receipt" style={{ fontSize: 16, color: "var(--pr)" }} />
            Historique des versements
          </div>

          {loadingVersements ? (
            <div style={{
              padding: 20, textAlign: "center",
              fontFamily: "Inter, sans-serif", fontSize: 12, color: "var(--m)",
            }}>
              Chargement…
            </div>
          ) : versements.length === 0 ? (
            <div style={{
              padding: 14, borderRadius: 12, background: "var(--s2)",
              textAlign: "center", fontFamily: "Inter, sans-serif", fontSize: 12, color: "var(--m)",
            }}>
              Aucun versement enregistré
            </div>
          ) : (
            versements.map((v) => {
              const meta = getStatutMeta(v.statut);
              return (
                <div key={v.id} style={{
                  padding: 12, borderRadius: 12, background: "var(--s2)",
                  marginBottom: 8,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 10,
                      background: meta.bg,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0,
                    }}>
                      <i className={meta.icon} style={{ fontSize: 18, color: meta.color }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontFamily: "Inter, sans-serif", fontSize: 13, fontWeight: 600, color: "var(--t)",
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                      }}>
                        {v.type_frais_libelle || "Versement"}
                      </div>
                      <div style={{ fontFamily: "Inter, sans-serif", fontSize: 11, color: "var(--m)", marginTop: 2 }}>
                        {formatDate(v.date_versement)} · {v.mode_paiement_libelle || "-"}
                      </div>
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={{ fontFamily: "Poppins, sans-serif", fontSize: 13, fontWeight: 700, color: "var(--t)" }}>
                        {formatMontant(v.montant)}
                      </div>
                      <div style={{ fontFamily: "Inter, sans-serif", fontSize: 10, color: "var(--h)" }}>
                        FCFA
                      </div>
                    </div>
                  </div>

                  <div style={{
                    display: "flex", alignItems: "center", gap: 8, marginTop: 10,
                    paddingTop: 10, borderTop: "1px solid var(--b)",
                  }}>
                    <div style={{
                      padding: "3px 8px", borderRadius: 10, background: meta.bg,
                      fontFamily: "Inter, sans-serif", fontSize: 10, fontWeight: 600, color: meta.color,
                      display: "flex", alignItems: "center", gap: 3,
                    }}>
                      <i className={meta.icon} style={{ fontSize: 11 }} />
                      {meta.label}
                    </div>
                    {v.numero_recu && (
                      <div style={{
                        fontFamily: "Inter, sans-serif", fontSize: 10, color: "var(--h)",
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                        flex: 1, minWidth: 0,
                      }}>
                        N° {v.numero_recu}
                      </div>
                    )}
                  </div>

                  {v.motif_annulation && (
                    <div style={{
                      marginTop: 8, padding: "6px 10px", borderRadius: 8,
                      background: "rgba(220,53,69,0.08)",
                      fontFamily: "Inter, sans-serif", fontSize: 11, color: "var(--rd)",
                    }}>
                      <i className="ti ti-info-circle" style={{ fontSize: 12, marginRight: 4 }} />
                      {v.motif_annulation}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

// ─── STYLES ───────────────────────────────────────────────────────────────
function SituationStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;700&family=Inter:wght@400;500;600;700&display=swap');

      *, *::before, *::after { box-sizing: border-box; }

      ::-webkit-scrollbar { width: 4px; }
      ::-webkit-scrollbar-thumb { background: var(--b2); border-radius: 4px; }

      @keyframes skPulse {
        from { opacity: 0.55; }
        to { opacity: 1; }
      }

      .pa-situation-error {
        background: #fff;
        border-radius: 20px;
        padding: 40px 20px;
        text-align: center;
        box-shadow: 0 4px 18px rgba(0,0,0,0.08);
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 10px;
        margin: 16px;
      }
      .pa-situation-error i.alert-icon {
        font-size: 48px;
        color: var(--rd);
      }
      .pa-situation-error h2 {
        font-family: 'Poppins', sans-serif;
        font-size: 17px; font-weight: 600;
        color: var(--t); margin: 4px 0 0;
      }
      .pa-situation-error p {
        font-family: 'Inter', sans-serif;
        font-size: 13px; color: var(--m);
        margin: 0 0 12px;
      }
      .pa-situation-error .actions-row {
        display: flex; gap: 8px;
        flex-wrap: wrap; justify-content: center;
      }
      .pa-situation-error .btn {
        border: none; border-radius: 30px;
        padding: 10px 22px; cursor: pointer;
        font-family: 'Inter', sans-serif;
        font-size: 13px; font-weight: 600;
        color: #fff; background: var(--pr);
        display: inline-flex; align-items: center; gap: 6px;
        transition: opacity 0.2s;
      }
      .pa-situation-error .btn:hover { opacity: 0.9; }
      .pa-situation-error .btn.btn-secondary {
        background: var(--b2); color: var(--t);
      }
    `}</style>
  );
}

// ─── PAGE PRINCIPALE ──────────────────────────────────────────────────────
export default function PASituationFrais({ slug }) {
  const navigate = useNavigate();
  const { getSituationTousEnfants } = useParentService();
  const { openDrawer } = useParentDrawer();

  const [situations, setSituations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const hasFetchedRef = useRef(false);

  // ─── CHARGEMENT ────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!slug) {
      setError("Identifiant parent manquant.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await getSituationTousEnfants(slug);
      setSituations(data || []);
    } catch (err) {
      setError("Impossible de récupérer la situation financière.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    loadData();
  }, [loadData]);

  // ─── RENDU ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <>
        <SituationStyles />
        <SkeletonLoader />
      </>
    );
  }

  // Y a-t-il au moins un enfant inscrit ?
  const avecInscription = situations.filter(s => s.statut !== "AUCUNE_INSCRIPTION");

  return (
    <>
      <SituationStyles />

      <div style={{ background: "var(--s2)", minHeight: "100vh", paddingBottom: 30 }}>
        {/* Header TOUJOURS affiché (menu accessible) */}
        <Header openDrawer={openDrawer} />

        {error ? (
          /* ── ÉTAT ERREUR (header conservé au-dessus) ── */
          <div className="pa-situation-error">
            <i className="ti ti-alert-circle alert-icon" />
            <h2>Erreur de chargement</h2>
            <p>{error}</p>
            <div className="actions-row">
              <button className="btn" onClick={loadData}>
                <i className="ti ti-refresh" />
                Réessayer
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => navigate(`/${slug}/parent/accueil`)}
              >
                <i className="ti ti-home" />
                Accueil
              </button>
            </div>
          </div>
        ) : situations.length === 0 ? (
          /* ── ÉTAT VIDE ── */
          <div style={{
            margin: 32, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center",
          }}>
            <div style={{
              width: 80, height: 80, borderRadius: "50%", background: "rgba(27,79,128,0.1)",
              display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20,
            }}>
              <i className="ti ti-wallet" style={{ fontSize: 40, color: "var(--pr)" }} />
            </div>
            <div style={{ fontFamily: "Poppins, sans-serif", fontSize: 17, fontWeight: 600, color: "var(--t)" }}>
              Aucune donnée financière
            </div>
            <div style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "var(--m)", marginTop: 8 }}>
              Les informations apparaîtront ici dès qu'une inscription sera enregistrée.
            </div>
          </div>
        ) : (
          /* ── CONTENU ── */
          <>
            {/* Vue d'ensemble uniquement quand plusieurs enfants sont inscrits */}
            {avecInscription.length > 1 && <StatsGlobales situations={avecInscription} />}
            {avecInscription.length === 1 && <EnfantInfoBar situation={avecInscription[0]} />}

            <div style={{ padding: "0 16px" }}>
              {situations.map((s, i) => (
                <EnfantCard
                  key={s.eleve_id ?? i}
                  situation={s}
                  slug={slug}
                  navigate={navigate}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
}